import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography
} from "@mui/material";
import {
  CheckmarkCircle20Px,
  CloseCircle20Px,
  useToast
} from "@qubit/autoparts";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { AutostoreBin } from "~/features/autostoreBin";
import {
  openSanitizeModal,
  removeSanitizedBin
} from "~/features/autostorePutaway/autostorePutaway.slice";
import { SanitizeModal } from "~/features/autostorePutaway/modals/SanitizeModal";
import {
  mixpanelTrack,
  mixpanelTrackModalDismiss,
  useMixpanelModalName,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  selectIsShortSideBin,
  selectThisWorkstation,
  selectWorkstationAutostoreGridId
} from "~/redux/selectors/workstationsSelectors";
import { useConfirmInventoryMovementMutation } from "~/redux/warehouse/autostoreBinMaintenance.hooks";
import { usePostFlagBinMutation } from "~/redux/warehouse/autostoreGrid.hooks";
import { usePostInventoryMoveMutation } from "~/redux/warehouse/inventory.hooks";
import { useVariantsByIdQuery } from "~/redux/warehouse/variant.hooks";
import { InventoryDto } from "~/types/api";

import { AdjustButton } from "./AdjustButton";
import { PulsingArrows } from "./PulsingArrows";
import {
  setIsMoveAdjustModalOpen,
  setIsMoveErrorModalOpen,
  setSourceDestinationBins,
  setTaskState
} from "./binMaintenanceWorkstation.slice";

export function MoveInventoryButton({
  sourceBinInventory,
  disabled,
  portId,
  refetchBinInventory
}: {
  sourceBinInventory: InventoryDto[];
  disabled: boolean;
  portId: number;
  refetchBinInventory: () => void;
}) {
  const isShortSideBin = useAppSelector(selectIsShortSideBin);
  const selectedWorkstation = useAppSelector(selectThisWorkstation);
  const autostoreGridId = useAppSelector(selectWorkstationAutostoreGridId);
  const { movementData, isDestinationBinReady } = useAppSelector(
    (state) => state.binMaintenanceWorkstation
  );
  const sanitizedBins = useAppSelector(
    (state) => state.autostorePutaway.sanitizedBins
  );

  const { inventoryMovements } = movementData || {};
  const { destinationBin, sourceBin } =
    inventoryMovements?.sourceAndDestinationBin || {};
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { errorToast } = useToast();
  const trackedPageName = useMixpanelPageName();
  const trackedModalName = useMixpanelModalName("Move Inventory");

  const { data: variantsData, isLoading: isLoadingVariantsData } =
    useVariantsByIdQuery({
      variantIds: sourceBinInventory.map((bin) => bin.variantId)
    });

  const [postInventoryMove, { isLoading: inventoryMoveIsLoading }] =
    usePostInventoryMoveMutation();
  const [postFlagBin, { isLoading: flagBinIsLoading }] =
    usePostFlagBinMutation();

  const [
    confirmInventoryMovement,
    { isLoading: confirmInventoryMovementIsLoading }
  ] = useConfirmInventoryMovementMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const sourceCompartmentInventory =
    inventoryMovements &&
    sourceBinInventory.find(
      (bin) => bin.inventoryId === sourceBin?.inventoryId
    );
  const sourceCompartmentVariant = variantsData?.find(
    (variant) => variant.variantId === sourceCompartmentInventory?.variantId
  );
  const { isFoodSafetyItem, isSanitizeTargetBin } =
    sourceCompartmentVariant || {};
  const isFinalCompartmentToMove = sourceBinInventory.length === 1;
  const isDestinationBinWhole =
    destinationBin?.horizontalCompartmentCount === 1 &&
    destinationBin.verticalCompartmentCount === 1;

  const confirmMove = async (inventoryAdjustedToZero: boolean) => {
    if (
      !sourceCompartmentInventory ||
      !autostoreGridId ||
      !selectedWorkstation ||
      !sourceBin ||
      !destinationBin?.warehouseBinId ||
      inventoryMoveIsLoading ||
      confirmInventoryMovementIsLoading ||
      flagBinIsLoading
    )
      return;
    const wasTargetBinSanitized = sanitizedBins.includes(
      destinationBin.autostoreBinNumber
    );
    // if inventory was adjusted to zero, skip the postInventoryMove call
    if (!inventoryAdjustedToZero) {
      try {
        await postInventoryMove({
          inventoryId: sourceCompartmentInventory?.inventoryId,
          targetBinId: destinationBin.warehouseBinId,
          quantity: sourceCompartmentInventory?.count,
          workstationId: selectedWorkstation.id,
          wasTargetBinSanitized: wasTargetBinSanitized || false
        }).unwrap();
        dispatch(removeSanitizedBin(destinationBin?.autostoreBinNumber));
      } catch {
        dispatch(setIsMoveErrorModalOpen(true));
        setIsModalOpen(false);
        Sentry.captureMessage(
          `Move inventory error. Inventory: ${sourceCompartmentInventory?.inventoryId}, SourceBin: ${sourceBin.autostoreBinNumber}, DestinationBin: ${destinationBin.autostoreBinNumber}`,
          "error"
        );
        return;
      }
    }

    try {
      if (isFoodSafetyItem) {
        await postFlagBin({
          autostoreGridId,
          portId,
          binId: sourceBin.autostoreBinNumber,
          reason: "food safety"
        }).unwrap();
      }
      // after moving the inventory on final compartment, close the modal so user can unflag the source bin before confirmingInventoryMovement
      if (isFinalCompartmentToMove) {
        setIsModalOpen(false);
        refetchBinInventory();
        dispatch(setTaskState("ConfirmingMovement"));
        return;
      }

      const nextInventoryMovement = await confirmInventoryMovement({
        autostoreGridId,
        workstationId: selectedWorkstation.id,
        sourceBinId: sourceBin.autostoreBinNumber,
        destinationBinId: destinationBin.autostoreBinNumber
      }).unwrap();
      dispatch(setSourceDestinationBins(nextInventoryMovement));
      refetchBinInventory();
    } catch (error) {
      errorToast(getMessageFromRtkError(error), {
        trackingLocation: trackedModalName
      });
    }
  };

  useEffect(() => {
    // in case a sanitze product somehow makes it into a non-whole bin,
    // open the adjust modal to remove the product
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (isModalOpen && isSanitizeTargetBin && !isDestinationBinWhole) {
      dispatch(setIsMoveAdjustModalOpen(true));
    }
  }, [isSanitizeTargetBin, isDestinationBinWhole, dispatch, isModalOpen]);

  return (
    <>
      <Button
        size="large"
        onClick={() => {
          mixpanelTrack({
            type: "Button Click",
            label: "Move Inventory",
            trackedPageName
          });
          if (isSanitizeTargetBin && isDestinationBinWhole) {
            dispatch(openSanitizeModal());
            return;
          }
          setIsModalOpen(true);
        }}
        disabled={disabled || !isDestinationBinReady}
      >
        {t("move inventory")}
      </Button>
      <Dialog
        open={isModalOpen}
        onClose={(_event, reason) => {
          mixpanelTrackModalDismiss(trackedModalName, reason);
          setIsModalOpen(false);
        }}
        maxWidth="md"
      >
        <DialogTitle>{t("move inventory")} </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="overline">{t("product name")}</Typography>
          {isLoadingVariantsData || !sourceCompartmentVariant ? (
            <Skeleton variant="text" width="50%" />
          ) : (
            <Typography variant="body1">
              {sourceCompartmentVariant?.productName}
            </Typography>
          )}
        </DialogContent>
        {sourceBin && destinationBin && sourceCompartmentInventory && (
          <Stack direction="row" spacing={2} m={4}>
            <Stack
              direction="column"
              flex={1}
              minWidth="300px"
              minHeight={isShortSideBin ? "300px" : "initial"}
              alignItems="center"
            >
              <AutostoreBin
                state="Bin Opened"
                pickQuantity={sourceCompartmentInventory.count.value}
                pickCompartment={sourceBin.compartmentNumber - 1}
                numberOfRows={sourceBin.horizontalCompartmentCount}
                numberOfColumns={sourceBin.verticalCompartmentCount}
                isShortSideBin={isShortSideBin}
              />
              <Typography alignSelf="center">
                {t("bin") + ` ${sourceBin.autostoreBinNumber}`}
              </Typography>
            </Stack>
            <Box pb={3} alignContent="center">
              <PulsingArrows />
            </Box>
            <Stack
              direction="column"
              flex={1}
              minWidth="300px"
              alignItems="center"
            >
              <AutostoreBin
                state="Bin Opened"
                pickQuantity={""}
                pickCompartment={destinationBin.compartmentNumber - 1}
                numberOfRows={destinationBin.horizontalCompartmentCount}
                numberOfColumns={destinationBin.verticalCompartmentCount}
                moveTextCompartment={destinationBin.compartmentNumber}
                onBinClick={async (compartment) => {
                  if (compartment === destinationBin.compartmentNumber - 1) {
                    await confirmMove(false);
                    mixpanelTrack({
                      trackedPageName: trackedModalName,
                      type: "Button Click",
                      label: "Confirm Move Inventory - bin press"
                    });
                  }
                }}
                isShortSideBin={isShortSideBin}
              />
              <Typography alignSelf="center">
                {t("bin") + ` ${destinationBin.autostoreBinNumber}`}
              </Typography>
            </Stack>
          </Stack>
        )}
        <DialogActions sx={{ mb: 2, flexDirection: "column" }}>
          <Stack direction="row" mb={4}>
            {sourceBin && (
              <AdjustButton
                portId={portId}
                state="Bin Opened"
                binId={sourceBin.autostoreBinNumber}
                numberOfRows={sourceBin.horizontalCompartmentCount}
                numberOfColumns={sourceBin.verticalCompartmentCount}
                binInventory={sourceBinInventory}
                compartmentNumber={sourceBin.compartmentNumber}
                skipMove={() => confirmMove(true)}
                clearAllInventory={
                  isSanitizeTargetBin && !isDestinationBinWhole
                }
                closeMoveInventoryModal={() => setIsModalOpen(false)}
              />
            )}
          </Stack>
          <Stack justifyContent="center" direction="row" width="100%" gap={4}>
            <Button
              variant="subtle"
              size="large"
              startIcon={<CloseCircle20Px />}
              onClick={() => {
                setIsModalOpen(false);
                mixpanelTrack({
                  type: "Button Click",
                  label: "Cancel",
                  trackedPageName: trackedModalName
                });
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<CheckmarkCircle20Px fill="white" />}
              onClick={async () => {
                await confirmMove(false);
                mixpanelTrack({
                  trackedPageName: trackedModalName,
                  type: "Button Click",
                  label: "Confirm"
                });
              }}
              disabled={
                inventoryMoveIsLoading || confirmInventoryMovementIsLoading
              }
            >
              {t("confirm")}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
      {destinationBin && (
        <SanitizeModal
          onConfirm={() => setIsModalOpen(true)}
          binNumber={destinationBin?.autostoreBinNumber}
        />
      )}
    </>
  );
}
